import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Search } from 'lucide-react';

import BathroomImg from '@/assets/style-settings/room_Bathroom.webp';
import BedroomImg from '@/assets/style-settings/room_Bedroom.webp';
import DiningRoomImg from '@/assets/style-settings/room_DiningRoom.webp';
import KitchenImg from '@/assets/style-settings/room_Kitchen.webp';
import KitchenLivingComboImg from '@/assets/style-settings/room_KitchenLivingCombo.webp';
import LivingRoomImg from '@/assets/style-settings/room_LivingRoom.webp';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import { ROOM_TYPE_OPTIONS } from '../../constants/style-settings';

export interface RoomTypeSelectionContentProps {
  roomType?: ROOM_TYPE_OPTIONS;
  handleUpdateRoomType: (val?: ROOM_TYPE_OPTIONS) => void;
}

const roomTypeOptions = [
  { value: ROOM_TYPE_OPTIONS.LIVING_ROOM, image: LivingRoomImg },
  { value: ROOM_TYPE_OPTIONS.BEDROOM, image: BedroomImg },
  { value: ROOM_TYPE_OPTIONS.KITCHEN, image: KitchenImg },
  { value: ROOM_TYPE_OPTIONS.DINING_ROOM, image: DiningRoomImg },
  { value: ROOM_TYPE_OPTIONS.KITCHEN_LIVING_COMBO, image: KitchenLivingComboImg },
  { value: ROOM_TYPE_OPTIONS.BATHROOM, image: BathroomImg },
];

export const RoomTypeSelectionContent = ({
  roomType,
  handleUpdateRoomType,
}: RoomTypeSelectionContentProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState<string | undefined>();

  const filteredRoomTypeOptions = useMemo(
    () =>
      !search
        ? roomTypeOptions
        : roomTypeOptions.filter((option) =>
            t(option).toLowerCase().includes(search.toLowerCase())
          ),
    [t, search]
  );

  return (
    <div className="w-full">
      <div className="flex h-[40px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6 lg:h-[60px]">
        <span className="text-sm font-semibold text-white">{t('roomType')}</span>
      </div>
      <div className="bg-white p-2 sm:px-5 sm:py-6">
        <Input
          size="sm"
          className="border border-solid border-primary-light"
          placeholder={t('search')}
          value={search}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
          startIcon={<Search className="text-primary-light" />}
        />
        <div className="mt-3 grid max-h-calc-full-minus-100 grid-cols-2 gap-2 overflow-y-auto sm:max-h-[400px] lg:max-h-[472px]">
          {filteredRoomTypeOptions.map((option, index) => {
            const isSelected = option.value === roomType;

            return (
              <div
                key={`desired-style-option-${index}`}
                className={cn(
                  'relative flex cursor-pointer flex-col overflow-hidden rounded-sm border border-solid',
                  !isSelected ? 'border-text-primary' : 'border-primary-light'
                )}
                onClick={() => handleUpdateRoomType(option.value)}
              >
                <div className="flex-1">
                  <img className="h-[84px] w-full object-cover" src={option.image} />
                </div>
                <div
                  className={`z-2 flex h-[30px] items-center justify-center ${isSelected ? 'bg-primary-light' : 'bg-text-primary'}`}
                >
                  <span className="text-center text-sm font-semibold leading-none text-white">
                    {t(option.value)}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
